"use client";

import { useCallback, useState } from "react";
import {
  Divider,
  DropdownSelection,
  DropdownSelectionOption,
  ListItem,
  Text,
} from "@stone-payments/jade";
import { Mask } from "@/utils/masks";
import { Skeleton } from "@/components/skeleton";
import { productAnalitica } from "@/app/(store)/_utils/events";
import { useProductStore } from "@/stores/product-store";
import { ExperimentsFlags } from "@/services/amplitude/experiment";
import { useExperiment } from "@/contexts/analitica";
import { useVitrineStore } from "@/contexts/vitrine-provider";

type Props = {
  loading?: boolean;
};

const Loading = () => {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton height="h-10" />
      <Skeleton height="h-10" />
    </div>
  );
};

export const ProductVariants = ({ loading = false }: Props) => {
  const preCheckout = useVitrineStore(
    (state) => state.store.vitrine.preCheckout
  );
  const {
    product,
    setPrice,
    setStock,
    chosenVariants,
    setChosenVariants,
    setId,
  } = useProductStore();
  const [openedDropdown, setOpenedDropdown] = useState<string | null>(null);

  const stockExperiment = useExperiment(ExperimentsFlags.Stock);

  const { variationsTypes: types = [], variants } = product || {};

  const findVariantByOptions = useCallback(
    (optionId: string, typeId: string) => {
      const options = Object.keys(chosenVariants).reduce((acc, key) => {
        if (key !== typeId) acc.push(chosenVariants[key].id);
        return acc;
      }, [] as string[]);

      return variants?.find((variant) => {
        const variantOptionIds = variant.chosenVariationOptions.map(
          (opt) => opt.option.id
        );
        return (
          variantOptionIds.includes(optionId) &&
          options.every((id) => variantOptionIds.includes(id))
        );
      });
    },
    [chosenVariants, variants]
  );

  const setChosenVariant = useCallback(
    (type: { id: string; name: string }, option: DropdownSelectionOption) => {
      const currentVariant = findVariantByOptions(option.value, type.id);
      queueMicrotask(() => {
        setPrice(currentVariant!.price.sale || 0);
        setStock(currentVariant!.stock?.current || 0);
        setId(currentVariant!.id);

        setChosenVariants(type.id, {
          id: option.value,
          name: option.label,
          type: type.name,
        });

        setOpenedDropdown(null);
      });
    },
    [setChosenVariants, setPrice, setStock, setId, findVariantByOptions]
  );

  const checkIfVariantIsAvailable = useCallback(
    (variant: (typeof variants)[number] | undefined) => {
      if (!variant) return false;

      const hasStockFeatureEnabled =
        stockExperiment?.key === "on" && preCheckout.stock.enabled;
      const isAvailable = variant?.stock?.current && variant.stock.current > 0;

      return hasStockFeatureEnabled ? isAvailable : true;
    },
    [preCheckout, stockExperiment]
  );

  const getPriceForOption = (typeId: string, optionId: string) => {
    const currentVariant = findVariantByOptions(optionId, typeId);

    return checkIfVariantIsAvailable(currentVariant)
      ? Mask.formatToBrlCurrency(currentVariant?.price.sale || 0)
      : "Indisponível";
  };

  const onSelectVariant = (typeId: string) => {
    productAnalitica.events.action.variationClicked();

    setOpenedDropdown(typeId);
  };

  if (loading) return <Loading />;

  return Object.keys(chosenVariants).length > 0 ? (
    <section aria-label="Variações do produto" data-testid="product-variants">
      {types?.map((type, index) => (
        <div key={type.id}>
          <DropdownSelection
            title={type.name}
            showActions={false}
            type="single"
            toggleOpen={() => {}}
            open={openedDropdown === type.id}
            onCancel={() => setOpenedDropdown(null)}
            options={type.existentVariationOptions.map((option) => ({
              label: option.name,
              value: option.id,
              description:
                index === types.length - 1
                  ? getPriceForOption(type.id, option.id)
                  : "",
              selected: chosenVariants[type.id].id === option.id,
            }))}
            onOptionChange={(option) => setChosenVariant(type, option[0])}
            trigger={
              <ListItem
                className="mx-0! w-full! p-0!"
                content={<ListItem.Content label={type.name} />}
                trailing={
                  <Text color="medium" variant="text-medium" weight="medium">
                    {chosenVariants[type.id].name}
                  </Text>
                }
                onClick={() => onSelectVariant(type.id)}
                navigation
              />
            }
          />
          <Divider />
        </div>
      ))}
    </section>
  ) : null;
};
